<template>
  <d-modal
    :active="isActive"
    :width="1400"
    @close="handleClose"
    :can-cancel="['escape', 'x', 'outside']"
  >
    <div class="p-6">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <div>
          <h3 class="text-xl font-semibold text-secondary-500">
            {{ exam.name }}
          </h3>
          <h2 class="text-4xl font-bold">
            Manage Exam Questions
          </h2>
        </div>
        <div class="flex gap-2">
          <d-btn 
            color="secondary" 
            size="sm" 
            @click="importQuestions"
            :loading="importing"
            :disabled="importing"
          >
            Import Questions
          </d-btn>
          <d-btn color="secondary" size="sm" @click="exportQuestions">
            Export Questions
          </d-btn>
        </div>
      </div>

      <!-- Import Errors Section -->
      <div v-if="importErrors.length > 0" class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex justify-between items-center mb-3">
          <h4 class="text-lg font-semibold text-red-800">Import Validation Errors</h4>
          <d-btn size="sm" flat @click="clearImportErrors">×</d-btn>
        </div>
        <div class="text-sm text-red-700 mb-3">
          The following validation errors were found. No questions were imported.
        </div>
        <div class="max-h-48 overflow-y-auto">
          <ul class="list-disc list-inside space-y-1">
            <li v-for="error in importErrors" :key="error" class="text-sm text-red-700">
              {{ error }}
            </li>
          </ul>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Left Panel: Questions List -->
        <div class="space-y-4">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-semibold">Questions List</h3>
            <d-btn color="success" @click="addQuestion">
              + Add Question
            </d-btn>
          </div>
          
          <div class="max-h-96 overflow-y-auto border rounded-lg">
            <div v-if="questions.length === 0" class="text-center py-8 text-gray-500">
              No questions added yet. Click "Add Question" to get started.
            </div>
            
            <div v-else class="space-y-2 p-2">
              <div 
                v-for="(question, index) in questions" 
                :key="question.id" 
                class="flex items-center gap-2"
              >
                <!-- Question Content (Clickable) -->
                <div 
                  class="flex-1 p-3 bg-white border rounded-lg hover:bg-gray-50 cursor-pointer"
                  :class="{ 'border-blue-300 bg-blue-50': selectedQuestion?.id === question.id }"
                  @click="editQuestion(question)"
                >
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <p class="font-bold text-sm mb-1">
                        #{{ index + 1 }} - {{ question.content }}
                      </p>
                      <p class="text-xs text-gray-600">
                        Type: {{ question.type }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Reorder Controls (Non-clickable) -->
                <div class="flex gap-1">
                  <d-btn 
                    size="sm" 
                    @click="moveQuestionUp(index)" 
                    :disabled="index === 0"
                    title="Move Up"
                  >
                    ↑
                  </d-btn>
                  <d-btn 
                    size="sm" 
                    @click="moveQuestionDown(index)" 
                    :disabled="index === questions.length - 1"
                    title="Move Down"
                  >
                    ↓
                  </d-btn>
                </div>

                <!-- Delete Button -->
                <div>
                  <d-btn 
                    size="sm" 
                    color="error" 
                    @click="deleteQuestion(question.id)"
                    title="Delete Question"
                  >
                    🗑️
                  </d-btn>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Panel: Question Form -->
        <div class="space-y-4">
          <h3 class="text-lg font-semibold">
            {{ selectedQuestion ? 'Edit Question' : 'Add New Question' }}
          </h3>
          
          <form @submit.prevent="saveForm()" class="space-y-4">
            <div class="border rounded-lg p-4 space-y-4">
              <d-field label="Question Text">
                <d-input v-model="form.content" />
              </d-field>

              <d-field label="Question Type" class="w-full">
                <d-select v-model="form.type" required>
                  <option value="Multiple Choice">Multiple Choice</option>
                  <option value="Text">Text Response</option>
                  <option value="Drop Down">Drop Down</option>
                  <option value="Multiple Answer">Multiple Answer</option>
                  <option value="File Upload">File Upload</option>
                  <option value="True/False">True/False</option>
                </d-select>
              </d-field>

              <!-- Question Type Instructions -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p v-if="form.type == 'Multiple Answer'" class="text-sm text-blue-800">
                  Add as many choices as desired, and select multiple correct answer choices. De-select a choice to remove it.
                </p>
                <p v-if="form.type == 'Drop Down' || form.type == 'Multiple Choice'" class="text-sm text-blue-800">
                  Add as many choices as desired, and select one correct answer choice
                </p>
                <p v-if="form.type == 'File Upload'" class="text-sm text-blue-800">
                   Users will be able to upload files as their answer
                </p>
                <p v-if="form.type == 'Text'" class="text-sm text-blue-800">
                   Users will be able to supply free text as their answer
                </p>
                <p v-if="form.type == 'True/False'" class="text-sm text-blue-800">
                   Users will select True or False
                </p>
              </div>

              <!-- Answer Choices Section -->
              <div v-if="form.type == 'Multiple Choice' || form.type == 'Drop Down' || form.type == 'Multiple Answer'" class="space-y-3">
                <label class="block text-sm font-medium text-gray-700">Answer Choices</label>
                <div 
                  v-for="(choice, index) in form.answerChoices" 
                  :key="index"
                  class="flex items-center space-x-2 p-2 bg-gray-50 rounded-md"
                >
                  <d-input 
                    v-model="choice.content" 
                    placeholder="Enter answer choice"
                    class="flex-1"
                    size="sm"
                  />
                  <input 
                    v-if="form.type === 'Multiple Answer'"
                    type="checkbox"
                    v-model="choice.isCorrect"
                    class="rounded"
                  />
                  <input 
                    v-else
                    type="radio"
                    :name="'correct-answer'"
                    :value="index"
                    @change="setCorrectAnswer(index)"
                    :checked="choice.isCorrect"
                    class="rounded"
                  />
                  <d-btn 
                    size="sm" 
                    color="error" 
                    @click="removeChoice(index)"
                    :disabled="form.answerChoices.length <= 1"
                  >
                    🗑️
                  </d-btn>
                </div>
                <div>
                  <d-btn size="sm" color="secondary" @click="addChoice()">
                    + Add Choice
                  </d-btn>
                </div>
              </div>

              <!-- True/False Answer Section -->
              <div v-if="form.type == 'True/False'" class="space-y-3">
                <label class="block text-sm font-medium text-gray-700">Correct Answer</label>
                <div class="flex space-x-4">
                  <label class="flex items-center">
                    <input 
                      type="radio" 
                      value="true" 
                      v-model="correctTrueFalseAnswer"
                      class="mr-2"
                    />
                    True
                  </label>
                  <label class="flex items-center">
                    <input 
                      type="radio" 
                      value="false" 
                      v-model="correctTrueFalseAnswer"
                      class="mr-2"
                    />
                    False
                  </label>
                </div>
              </div>
            </div>

            <div class="flex justify-between pt-4">
              <d-btn 
                v-if="selectedQuestion" 
                flat 
                @click="cancelEdit"
              >
                Cancel Edit
              </d-btn>
              <div class="flex gap-2 ml-auto">
                <d-btn 
                  :loading="saving" 
                  type="submit" 
                  color="primary"
                >
                  {{ selectedQuestion ? 'Save Changes' : 'Add Question' }}
                </d-btn>
              </div>
            </div>
          </form>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end pt-6 border-t border-gray-200 mt-6">
        <div class="flex gap-2">
          <d-btn color="secondary" @click="handleClose">
            Save and Close
          </d-btn>
        </div>
      </div>
    </div>
  </d-modal>
</template>

<script>
import { getExamQuestions, exportExamQuestions, importExamQuestions } from '@resources/scripts/services/examsApi.service';
import { showErrorToast } from '@services/errorHandling.service';
import { toast } from '@services/toast.service';
import axios from 'axios';

export default {
  name: 'ExamQuestionListModal',
  
  props: {
    active: {
      type: Boolean,
      default: false
    },
    exam: {
      type: Object,
      default: () => ({})
    },
    contentListing: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ['close'],

  data() {
    return {
      isActive: false,
      questions: [],
      loading: false,
      importing: false,
      importErrors: [],
      selectedQuestion: null,
      saving: false,
      form: {
        examId: null,
        content: '',
        type: 'Multiple Choice',
        sortIndex: 0,
        answerChoices: [{
          content: '',
          sortIndex: 0,
          isCorrect: false,
        }],
      },
      emptyChoice: {
        content: '',
        sortIndex: 0,
        isCorrect: false,
      },
      correctTrueFalseAnswer: 'true',
    };
  },

  computed: {
    needsAnswerChoices() {
      return ['Multiple Choice', 'Drop Down', 'Multiple Answer'].includes(this.form.type);
    }
  },

  watch: {
    active: {
      immediate: true,
      handler(newVal) {
        this.isActive = newVal;
        if (newVal && this.exam.id) {
          this.loadQuestions();
          this.resetForm();
          this.clearImportErrors(); // Clear errors when modal opens
        }
      }
    },
    'form.type'() {
      this.handleTypeChange();
    },
    correctTrueFalseAnswer() {
      this.updateTrueFalseAnswers();
    }
  },

  methods: {
    async loadQuestions() {
      this.loading = true;
      try {
        const questions = await getExamQuestions(this.exam.id);
        // Sort questions by sortIndex to ensure proper order
        this.questions = questions.sort((a, b) => a.sortIndex - b.sortIndex);
      } catch (error) {
        console.error('Error loading exam questions:', error);
        showErrorToast(error);
      } finally {
        this.loading = false;
      }
    },

    handleClose() {
      this.isActive = false;
      this.resetForm();
      this.$emit('close');
    },

    resetForm() {
      this.selectedQuestion = null;
      this.form = {
        examId: this.exam.id,
        content: '',
        type: 'Multiple Choice',
        sortIndex: 0,
        answerChoices: [{
          content: '',
          sortIndex: 0,
          isCorrect: false,
        }],
      };
      this.correctTrueFalseAnswer = 'true';
    },

    addQuestion() {
      this.resetForm();
    },

    editQuestion(question) {
      this.selectedQuestion = question;
      this.form = JSON.parse(JSON.stringify(question));
      this.form.examId = this.exam.id;
      
      // Handle True/False questions
      if (this.form.type === 'True/False' && this.form.answerChoices?.length > 0) {
        const correctChoice = this.form.answerChoices.find(choice => choice.isCorrect);
        this.correctTrueFalseAnswer = correctChoice?.content?.toLowerCase() === 'true' ? 'true' : 'false';
      }
    },

    cancelEdit() {
      this.resetForm();
    },

    async deleteQuestion(questionId) {
      try {
        await this.$dialog.confirm({
          title: 'Delete Question',
          message: 'Are you sure you want to delete this question? This action cannot be undone.',
          confirmText: 'Delete',
          confirmColor: 'error',
        });

        // Call delete API
        // TODO: Implement delete question API endpoint
        console.log('Delete question:', questionId);
        await this.loadQuestions();
      } catch (error) {
        // User cancelled
      }
    },

    moveQuestionUp(index) {
      if (index > 0) {
        const questions = [...this.questions];
        [questions[index], questions[index - 1]] = [questions[index - 1], questions[index]];
        this.questions = questions;
        this.saveQuestionOrder();
      }
    },

    moveQuestionDown(index) {
      if (index < this.questions.length - 1) {
        const questions = [...this.questions];
        [questions[index], questions[index + 1]] = [questions[index + 1], questions[index]];
        this.questions = questions;
        this.saveQuestionOrder();
      }
    },

    async saveQuestionOrder() {
      try {
        console.log('Saving question order...', this.questions.map(q => ({ id: q.id, content: q.content.substring(0, 50), sortIndex: q.sortIndex })));
        
        // Update the sortIndex for each question based on its new position
        const updatePromises = this.questions.map(async (question, index) => {
          console.log(`Question ${question.id}: current sortIndex=${question.sortIndex}, new sortIndex=${index}`);
          
          // Always update to ensure the order is correct
          const updateData = {
            content: question.content,
            type: question.type,
            sortIndex: index,
            answerChoices: question.answerChoices || []
          };
          
          console.log(`Updating question ${question.id} with:`, updateData);
          // Use the exam questions endpoint instead of the quiz questions endpoint
          return await axios.patch(`/api/exams/questions/${question.id}`, updateData);
        });

        await Promise.all(updatePromises);
        
        console.log('All question updates completed');
        toast.success('Question order updated successfully');
        
        // Update local sortIndex values to match the new order
        this.questions.forEach((question, index) => {
          question.sortIndex = index;
        });
        
      } catch (error) {
        console.error('Error updating question order:', error);
        console.error('Full error details:', error.response);
        showErrorToast(error);
        // Reload questions to revert any local changes
        await this.loadQuestions();
      }
    },

    // Question form methods from ExamQuestionModal
    addChoice() {
      const copiedChoice = JSON.parse(JSON.stringify(this.emptyChoice));
      copiedChoice.sortIndex = this.form.answerChoices.length;
      this.form.answerChoices.push(copiedChoice);
    },

    removeChoice(index) {
      if (this.form.answerChoices.length > 1) {
        this.form.answerChoices.splice(index, 1);
        // Update sort indices
        this.form.answerChoices.forEach((choice, idx) => {
          choice.sortIndex = idx;
        });
      }
    },

    setCorrectAnswer(index) {
      // For single-answer questions, only one can be correct
      this.form.answerChoices.forEach((choice, idx) => {
        choice.isCorrect = idx === index;
      });
    },

    handleTypeChange() {
      if (this.form.type === 'True/False') {
        // Set up True/False choices
        this.form.answerChoices = [
          { content: 'True', sortIndex: 0, isCorrect: this.correctTrueFalseAnswer === 'true' },
          { content: 'False', sortIndex: 1, isCorrect: this.correctTrueFalseAnswer === 'false' }
        ];
      } else if (this.needsAnswerChoices && this.form.answerChoices.length === 0) {
        // Reset to default answer choices for other types
        this.form.answerChoices = [{
          content: '',
          sortIndex: 0,
          isCorrect: false,
        }];
      } else if (!this.needsAnswerChoices) {
        // Clear answer choices for types that don't need them
        this.form.answerChoices = [];
      }
    },

    updateTrueFalseAnswers() {
      if (this.form.type === 'True/False') {
        this.form.answerChoices = [
          { content: 'True', sortIndex: 0, isCorrect: this.correctTrueFalseAnswer === 'true' },
          { content: 'False', sortIndex: 1, isCorrect: this.correctTrueFalseAnswer === 'false' }
        ];
      }
    },

    saveForm() {
      // Validate the form before saving
      if (!this.validateForm()) {
        return;
      }
      this.createQuestion();
    },

    validateForm() {
      // Basic validation for question content
      if (!this.form.content || this.form.content.trim() === '') {
        toast.error('Please enter a question');
        return false;
      }

      // Validation for multiple choice, dropdown, and multiple answer questions
      if (this.needsAnswerChoices) {
        // Check if there are answer choices
        if (!this.form.answerChoices || this.form.answerChoices.length === 0) {
          toast.error('Please add at least one answer choice');
          return false;
        }

        // Check if at least one answer choice has content
        const hasValidChoices = this.form.answerChoices.some(choice => choice.content && choice.content.trim() !== '');
        if (!hasValidChoices) {
          toast.error('Please provide content for at least one answer choice');
          return false;
        }

        // Check if at least one correct answer is selected
        const hasCorrectAnswer = this.form.answerChoices.some(choice => choice.isCorrect);
        if (!hasCorrectAnswer) {
          toast.error('Please select at least one correct answer');
          return false;
        }

        // Check if Multiple Answer questions have at least two correct answers
        if (this.form.type === 'Multiple Answer') {
          const correctAnswerCount = this.form.answerChoices.filter(choice => choice.isCorrect).length;
          if (correctAnswerCount < 2) {
            toast.error('Please select at least two answers for a multiple selection question. If you only want to have one right answer, use the multiple choice question type.');
            return false;
          }
        }
      }

      return true;
    },

    async createQuestion() {
      this.saving = true;
      try {
        if (this.selectedQuestion && this.selectedQuestion.id) {
          await axios.patch(`/api/content-listings/items/interactive/questions/${this.selectedQuestion.id}`, this.form);
        } else {
          await axios.post(`/api/exams/${this.exam.id}/questions`, this.form);
        }
        await this.loadQuestions();
        this.resetForm();
        toast.success(this.selectedQuestion ? 'Question updated successfully' : 'Question added successfully');
      } catch (error) {
        showErrorToast(error);
      } finally {
        this.saving = false;
      }
    },

    async importQuestions() {
      try {
        // Clear any previous errors
        this.importErrors = [];
        
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.xlsx,.xls';
        input.onchange = async (event) => {
          const file = event.target.files[0];
          if (file) {
            this.importing = true;
            try {
              const result = await importExamQuestions(this.exam.id, file);
              
              if (result.success !== false) {
                // Import was successful
                this.$toast.success(`Successfully imported ${result.importedCount} questions`);
                if (result.errors && result.errors.length > 0) {
                  this.$toast.warning(`${result.errors.length} errors occurred during import`);
                  console.warn('Import errors:', result.errors);
                }
                if (result.skippedRows && result.skippedRows.length > 0) {
                  console.info('Skipped rows:', result.skippedRows);
                }
                await this.loadQuestions();
              } else {
                // Import failed due to validation errors
                this.importErrors = result.errors || [];
                this.$toast.error(`Import failed: ${result.errors?.length || 0} validation errors found`);
              }
            } catch (error) {
              showErrorToast(error);
            } finally {
              this.importing = false;
            }
          }
        };
        input.click();
      } catch (error) {
        showErrorToast(error);
        this.importing = false;
      }
    },

    async exportQuestions() {
      try {
        const response = await exportExamQuestions(this.exam.id);
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${this.exam.name}-questions-${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        showErrorToast(error);
      }
    },

    clearImportErrors() {
      this.importErrors = [];
    }
  }
};
</script>